import React, { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { navigateToAndroidSettings } from '../Common/KindredAndroidInterface/KindredAndroidInterface';

const Page = styled.main`
  display: flex;
  min-height: 100dvh;
  width: 100%;
  overflow-x: hidden;
  flex-direction: column;
  background: #ffffff;
`;

const CurvedHeader = styled.header`
  position: relative;
  display: flex;
  min-height: clamp(560px, calc(100dvh - 112px), 720px);
  box-sizing: border-box;
  overflow: hidden;
  flex-direction: column;
  align-items: center;
  padding: clamp(48px, 8dvh, 72px) 24px 0;
  background: #f5f6f8;
  color: #1b2457;
  text-align: center;
  clip-path: circle(
    clamp(450px, 110vw, 600px) at 50% calc(100% - clamp(450px, 110vw, 600px))
  );

  & > * {
    position: relative;
    z-index: 1;
  }

  @media (max-width: 390px) {
    min-height: 540px;
    padding: 44px 16px 0;
  }

  @media (min-width: 600px) {
    min-height: min(760px, calc(100dvh - 120px));
    padding-inline: 48px;
  }

  @media (max-height: 600px) and (orientation: landscape) {
    min-height: 440px;
    padding-top: 32px;
  }
`;

const Title = styled.h1`
  margin: 0;
  font-family: 'Fira Sans', sans-serif;
  font-size: clamp(35px, 5vw, 40px);
  font-weight: 700;
  line-height: 100%;
  letter-spacing: 0;

  @media (max-width: 390px) {
    font-size: 28px;
  }
`;

const Subtitle = styled.p`
  max-width: 420px;
  margin: 24px 0 28px;
  font-family: 'Fira Sans', sans-serif;
  font-size: clamp(18px, 3vw, 22px);
  font-weight: 400;
  line-height: 125%;
  letter-spacing: 0;
  color: #000000;

  @media (max-width: 390px) {
    max-width: 320px;
    margin: 18px 0 20px;
    font-size: 16px;
  }

  @media (max-height: 600px) and (orientation: landscape) {
    margin: 16px 0;
  }
`;

const InstructionImage = styled.img`
  display: block;
  width: auto;
  max-width: min(100%, 360px);
  height: clamp(300px, 52dvh, 460px);
  max-height: 60%;
  margin-top: auto;
  object-fit: contain;

  @media (max-width: 390px) {
    max-width: min(100%, 280px);
    height: clamp(250px, 46dvh, 340px);
  }

  @media (max-height: 600px) and (orientation: landscape) {
    height: 250px;
  }
`;

const ActionArea = styled.div`
  display: flex;
  flex: 1;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  padding: 24px clamp(20px, 5vw, 48px) max(28px, env(safe-area-inset-bottom));
`;

const ActionButton = styled.button`
  width: 100%;
  max-width: 640px;
  height: 56px;
  border: 0;
  border-radius: 5px;
  background: #52ae67;
  color: #ffffff;
  font-family: 'Fira Sans', sans-serif;
  font-size: 20px;
  font-weight: 700;
  cursor: pointer;

  @media (max-width: 390px) {
    height: 52px;
    font-size: 18px;
  }
`;

const stepOneImage = require('../Assets/KindredPluginActivation/androidInstructionsStepOne.png');
const stepTwoImage = require('../Assets/KindredPluginActivation/androidInstructionsStepTwo.png');

const KindredPluginActivationAndroidInstructions: React.FC = () => {
  const { t } = useTranslation();
  const [step, setStep] = useState<1 | 2>(1);
  const isFirstStep = step === 1;

  const handleAction = () => {
    if (isFirstStep) {
      setStep(2);
      return;
    }

    navigateToAndroidSettings();
  };

  return (
    <Page>
      <CurvedHeader>
        <Title>
          <Trans
            i18nKey="KindredPluginActivation.androidInstructions.title"
            t={t}
            components={{ br: <br /> }}
          />
        </Title>
        <Subtitle>
          <Trans
            i18nKey={`KindredPluginActivation.androidInstructions.${
              isFirstStep ? 'stepOneSubtitle' : 'stepTwoSubtitle'
            }`}
            t={t}
            components={{ strong: <strong /> }}
          />
        </Subtitle>
        <InstructionImage
          src={isFirstStep ? stepOneImage : stepTwoImage}
          alt={t(
            `KindredPluginActivation.androidInstructions.${
              isFirstStep ? 'stepOneImageAlt' : 'stepTwoImageAlt'
            }`
          )}
        />
      </CurvedHeader>
      <ActionArea>
        <ActionButton type="button" onClick={handleAction}>
          {t(
            `KindredPluginActivation.androidInstructions.${
              isFirstStep ? 'next' : 'enable'
            }`
          )}
        </ActionButton>
      </ActionArea>
    </Page>
  );
};

export default KindredPluginActivationAndroidInstructions;
